import styled, { css } from 'styled-components'

type Props = {
	/** Space between buttons */
	spacing?: number
	/** Column mode. All other positioning is still horizontal */
	column?: boolean
	/** Center buttons horizontally */
	center?: boolean
	/** Left-align buttons */
	left?: boolean
	/** Right-align buttons */
	right?: boolean
	/**
	 * Space around buttons. In column-mode, this is equivalent to center, since
	 * this is the closes match to row-mode behaviour .
	 */
	spaced?: boolean
	/**
	 * Space between buttons. In column-mode, this is equivalent to left, since
	 * this is the closes match to row-mode behaviour .
	 */
	spread?: boolean
	/** Stretch buttons to fill width */
	stretch?: boolean
}

/**
 * Simplified layout for buttons. Use `column` to show buttons on top of each
 * other. Vertical layout is always automatic. Specify one (and only one) of
 * `left` (default in row mode), `right`, `center`, `spaced`, `spread` or
 * `stretch` (default in column mode) for horizontal layout modes. Use `spacing`
 * (default 2) to control space between buttons.
 */
export const ButtonGroup = styled.div<Props>`
	display: flex;

	gap: ${({ spacing = 2 }) => spacing}px;

	${({ column, left, center, right, spaced, spread, stretch }) =>
		column
			? css`
			flex-direction: column;
			align-items: ${(() => {
				if (spaced || center) return 'center'
				if (left || spread) return 'flex-start'
				if (right) return 'flex-end'
				return 'stretch'
			})()};
		`
			: css`
			justify-content: ${(() => {
				if (spaced) return 'space-evenly'
				if (spread || stretch) return 'space-between'
				if (center) return 'center'
				if (right) return 'flex-end'
				return 'flex-start'
			})()};
		`}

	> * {
		margin: 0;
		flex-grow: ${({ stretch, column }) => (!column && stretch ? 1 : 0)};
	}

	/* Special case handling of right-aligning single element spread */
	${({ spread, right, column }) =>
		!column &&
		spread &&
		right &&
		css`
			&:has(> :first-child:last-child) {
				justify-content: flex-end;
			}
		`}
`
